import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Shield, LogOut, Home, Wifi, Car, AlertTriangle, Download, 
  Users, Radio, Camera, Menu, X, ChevronRight, Bell, Settings
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import useWebSocketAlerts from '../../hooks/useWebSocketAlerts';

const DashboardLayout = ({ children, title }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user, permissions } = useAuth();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [showAlerts, setShowAlerts] = useState(false);
  const { alerts, connected, mode, acknowledge, dismissAll, unreadCount } = useWebSocketAlerts();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { icon: Home, label: 'Overview', path: '/dashboard', tab: 'overview' },
    { icon: Wifi, label: 'Devices', path: '/dashboard', tab: 'devices' },
    { icon: Camera, label: 'Surveillance', path: '/dashboard', tab: 'surveillance' },
    { icon: Shield, label: 'IoT Security', path: '/dashboard', tab: 'iot' },
    { icon: Radio, label: '5G-IoT', path: '/dashboard', tab: '5g-iot' },
    { icon: Car, label: 'Automotive', path: '/dashboard', tab: 'automotive' },
    { icon: Download, label: 'Agents', path: '/dashboard', tab: 'agents' },
    { icon: AlertTriangle, label: 'Threats', path: '/dashboard', tab: 'threats' },
    { icon: Users, label: 'Team', path: '/dashboard', tab: 'team' },
  ];

  return (
    <div className="min-h-screen bg-[#1a1a1a]">
      {/* Top Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#242424]/90 backdrop-blur-xl border-b border-white/10">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Mobile menu button */}
            <button 
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-white/5 transition"
            >
              {sidebarOpen ? <X className="w-5 h-5 text-white" /> : <Menu className="w-5 h-5 text-white" />}
            </button>

            {/* Logo */}
            <div 
              className="flex items-center gap-3 cursor-pointer" 
              onClick={() => navigate('/')}
            >
              <img src="/infuse-logo.jpg" alt="Infuse.AI" className="h-9 w-9 rounded-lg" />
              <div>
                <span className="text-lg font-bold">
                  <span className="text-white">Infuse</span>
                  <span className="text-[#F26419]">.AI</span>
                  <span className="text-neutral-400 text-xs align-top">™</span>
                </span>
              </div>
            </div>
          </div>

          {/* Title - Desktop */}
          {title && (
            <h2 className="hidden md:block text-sm font-medium text-neutral-400">
              {title}
            </h2>
          )}

          {/* Right side */}
          <div className="flex items-center gap-4">
            {/* User info */}
            <div className="hidden sm:flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#F7B731] to-[#F26419] flex items-center justify-center">
                <span className="text-sm font-semibold text-black">
                  {(user?.email || 'U')[0]?.toUpperCase() || 'U'}
                </span>
              </div>
              <div className="hidden md:block">
                <p className="text-sm font-medium text-white">{user?.name || (user?.email ? user.email.split('@')[0] : 'User')}</p>
                <p className="text-xs text-neutral-500">{user?.email || 'user@company.com'}</p>
              </div>
            </div>

            {/* Admin Panel Button */}
            {permissions?.role === 'admin' && (
              <button
                data-testid="admin-panel-btn"
                onClick={() => navigate('/admin')}
                className="p-2 rounded-lg text-neutral-400 hover:text-white hover:bg-white/5 transition"
                title="Admin Panel"
              >
                <Settings className="w-5 h-5" />
              </button>
            )}

            {/* Real-Time Alerts Bell */}
            <div className="relative">
              <button
                data-testid="alerts-bell"
                onClick={() => setShowAlerts(!showAlerts)}
                className="relative p-2 rounded-lg text-neutral-400 hover:text-white hover:bg-white/5 transition"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
                {connected && <span className="absolute bottom-1 right-1 w-2 h-2 bg-green-400 rounded-full" />}
              </button>

              {showAlerts && (
                <div className="absolute right-0 top-12 w-96 max-h-96 bg-[#242424] border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50">
                  <div className="flex items-center justify-between p-3 border-b border-white/10">
                    <h3 className="text-white font-semibold text-sm">Live Threat Alerts</h3>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs ${connected ? 'text-green-400' : 'text-red-400'}`}>
                        {connected ? (mode === 'ws' ? 'Live' : 'Polling') : 'Disconnected'}
                      </span>
                      {alerts.length > 0 && (
                        <button onClick={dismissAll} className="text-xs text-neutral-500 hover:text-white">Clear</button>
                      )}
                    </div>
                  </div>
                  <div className="overflow-y-auto max-h-80">
                    {alerts.length === 0 ? (
                      <div className="p-6 text-center text-neutral-500 text-sm">No new alerts</div>
                    ) : (
                      alerts.slice(0, 10).map((alert) => (
                        <div key={alert.id} className="p-3 border-b border-white/5 hover:bg-white/5 transition cursor-pointer" onClick={() => acknowledge(alert.id)}>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-0.5">
                                <span className={`inline-block w-2 h-2 rounded-full ${
                                  alert.severity === 'critical' ? 'bg-red-500' :
                                  alert.severity === 'high' ? 'bg-orange-500' :
                                  alert.severity === 'medium' ? 'bg-yellow-500' : 'bg-blue-500'
                                }`} />
                                <span className="text-white text-sm font-medium">{alert.title}</span>
                              </div>
                              <p className="text-xs text-neutral-500">{alert.source} - {new Date(alert.timestamp).toLocaleTimeString()}</p>
                            </div>
                            <span className={`text-xs px-1.5 py-0.5 rounded ${
                              alert.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                              alert.severity === 'high' ? 'bg-orange-500/20 text-orange-400' :
                              alert.severity === 'medium' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-blue-500/20 text-blue-400'
                            }`}>{alert.severity}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Logout */}
            <button 
              onClick={handleLogout}
              data-testid="logout-btn"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm text-neutral-400 hover:text-white hover:bg-white/5 transition"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Mobile */}
      <aside className={`
        fixed top-0 left-0 bottom-0 w-64 bg-[#242424] border-r border-white/10 z-50 transform transition-transform duration-300 lg:hidden
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="p-4 pt-20">
          <nav className="space-y-1">
            {navItems.map((item) => (
              <button
                key={item.label}
                onClick={() => {
                  navigate(item.path);
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-neutral-400 hover:text-white hover:bg-white/5 transition text-left"
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </aside>

      {/* Main Content */}
      <main className="pt-16 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-neutral-500 mb-6">
            <button 
              onClick={() => navigate('/')}
              className="hover:text-white transition"
            >
              Home
            </button>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white">Dashboard</span>
          </div>

          {/* Page Content */}
          <div className="text-white">
            {children}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#242424] border-t border-white/5 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between">
          <p className="text-xs text-neutral-600">
            &copy; 2026 Infuse Technologies. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <a href="#" className="text-xs text-neutral-600 hover:text-neutral-400 transition">Privacy</a>
            <a href="#" className="text-xs text-neutral-600 hover:text-neutral-400 transition">Terms</a>
            <a href="#" className="text-xs text-neutral-600 hover:text-neutral-400 transition">Support</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DashboardLayout;
